<template>
  <div
    :class="{ 'form-popup-overlay': true, 'is-visible': isVisible, 'px-5': true }"
    @click="hide">
    <div
      :class="{ 'form-popup is-big': true, 'px-5': true, 'pb-4': true, 'pt-4': true, 'is-loading': loading }"
      @click.stop>
      <h3 class="text-center">
        Import {{ itemType }}
      </h3>

      <validation-observer ref="search-form" tag="form">
        <d-row>
          <div class="form-group align-items-center w-33 pr-3">
            <label class="d-block">
              <small>
                Date range:
              </small>
            </label>
            <date-range-picker
              ref="picker"
              :locale-data="{
                'firstDay': 1,
                'format': 'dMMMyy'
              }"
              class="text-center w-100"
              :singleDatePicker="false"
              :timePicker="false"
              :showWeekNumbers="false"
              :showDropdowns="true"
              :autoApply="true"
              v-model="filters.date_range"
              :ranges="false"
              :linkedCalendars="true">
              <template v-slot:input="picker" style="min-width: 350px;">
                <template v-if="picker.startDate === null || (picker.startDate.toString() !== picker.endDate.toString())">
                  {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
                </template>
                <template v-if="picker.startDate !== null && picker.startDate.toString() === picker.endDate.toString()">
                  {{ picker.startDate | dateStart }} - not specified
                </template>
              </template>
            </date-range-picker>
          </div>

          <div class="form-group align-items-center w-33 pr-3">
            <label class="d-block">
              <small>
                Departure airport:
              </small>
            </label>

            <div :class="{ 'form-control': true, 'is-select': true, 'w-100': true }">
              <v-select
                ref="airportsVSelectDeparture"
                :options="$root.enabledAirports"
                v-model="filters.departure_airport_id"
                :reduce="airport => airport.id"
                placeholder="Select an airport"
                :filterBy="filterByAirports"
                :maxItemsDisplay="100"
                label="id"
                class="w-100">
                <template slot="no-options">
                  <template>There are no airports for the provided phrase&hellip;</template>
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.icao }} ({{ option.name }})
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.icao }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="form-group align-items-center w-33">
            <label class="d-block">
              <small>
                Arrival airport:
              </small>
            </label>

            <div :class="{ 'form-control': true, 'is-select': true, 'w-100': true }">
              <v-select
                ref="airportsVSelectArrival"
                :options="$root.enabledAirports"
                v-model="filters.arrival_airport_id"
                :reduce="airport => airport.id"
                placeholder="Select an airport"
                :filterBy="filterByAirports"
                :maxItemsDisplay="100"
                label="id"
                class="w-100">
                <template slot="no-options">
                  <template>There are no airports for the provided phrase&hellip;</template>
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.icao }} ({{ option.name }})
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.icao }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="w-33 pr-3">
            <label class="d-block">
              <small>
                Flight number:
              </small>
            </label>

            <predictable-input
              ref="flightNumber"
              placeholder="Type flight number"
              labelField="number"
              endpoint="/trips/flights/items" />
          </div>

          <div class="w-33 pr-3">
            <label class="d-block">
              <small>
                Trip reference:
              </small>
            </label>

            <predictable-input
              ref="tripReference"
              placeholder="Type reference number"
              labelField="reference"
              endpoint="/trips/items" />
          </div>

          <div class="w-33 d-flex align-items-end">
            <d-button
              @click.prevent="validateForm"
              theme="success"
              class="mr-2 w-50">
              Search
            </d-button>

            <d-button
              @click.prevent="reset"
              outline
              theme="secondary"
              class="w-50">
              Reset
            </d-button>
          </div>
        </d-row>

        <d-row style="max-height: 50vh; overflow: auto;" class="mt-4">
          <table
            v-if="flights && flights.length"
            class="table">
            <thead style="position: sticky; top: 0; background-color: #eee;">
              <tr>
                <th style="width: calc(100% - 550px);" class="p-2">Number</th>
                <th style="width: 150px;" class="p-2">Airports</th>
                <th style="width: 150px;" class="p-2">Departure</th>
                <th style="width: 150px;" class="p-2">Arrival</th>
                <th style="width: 100px; " class="p-2 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(flight, index) of flights"
                :key="'flight-search-result-' + index">
                <td class="p-2 pt-3">{{ flight.number }}</td>
                <td class="p-2 pt-3">{{ flight.departure_airport.icao }} &rarr; {{ flight.arrival_airport.icao }}</td>
                <td class="p-2 pt-3"><pre class="mb-0">{{ flight.departure_date.substr(0, 16) }}</pre></td>
                <td class="p-2 pt-3"><pre class="mb-0">{{ flight.arrival_date.substr(0, 16) }}</pre></td>
                <td class="p-2 text-center">
                  <d-button
                    theme="primary"
                    size="sm"
                    :disabled="importInProgress"
                    @click.prevent="importItems(flight.id)">
                    Import
                  </d-button>
                </td>
              </tr>
            </tbody>
          </table>

          <d-alert
            v-if="flights && !flights.length"
            theme="warning"
            show
            class="w-100 mt-5 text-center">
            No flights matching the specified criteria...
          </d-alert>
        </d-row>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import DateUtils from './../../utils/DateUtils.js';
import FormUtils from './../../utils/FormUtils';
import PredictableInput from '../form-parts/PredictableInput.vue';

export default {
  name: 'ImportPaxItemsPopup',
  components: {
    'date-range-picker': DateRangePicker,
    'predictable-input': PredictableInput
  },
  filters: {
    dateStart (input) {
      if (input === null) {
        return ' from ';
      }

      let date = [
        input.getDate(),
        DateUtils.getMonthShortName(input.getMonth() + 1),
        input.getFullYear().toString().substr(2, 2)
      ].map(n => n < 10 ? '0' + n : n).join('');

      return date;
    },
    dateEnd (input) {
      if (input === null) {
        return ' to ';
      }

      let date = [
        input.getDate(),
        DateUtils.getMonthShortName(input.getMonth() + 1),
        input.getFullYear().toString().substr(2, 2)
      ].map(n => n < 10 ? '0' + n : n).join('');

      return date;
    }
  },
  data () {
    return {
      isVisible: false,
      loading: false,
      importInProgress: false,
      itemType: '',
      flightID: '',
      flights: null,
      filters: {
        date_range: {
          startDate: null,
          endDate: null
        },
        departure_airport_id: null,
        arrival_airport_id: null,
        number: '',
        trip_reference: ''
      }
    };
  },
  mounted () {
    this.$bus.$on('show-import-pax-items-popup', this.show);
  },
  methods: {
    show (payload) {
      this.itemType = payload.itemType;
      this.flightID = payload.flightID;

      setTimeout(() => {
        this.$refs['search-form'].reset();
        this.isVisible = true;
      }, 100);
    },
    validateForm () {
      FormUtils.validate(this.$refs['search-form'], this.submitForm);
    },
    submitForm () {
      this.loading = true;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/trips/flights/items',
        outputKey: 'flights',
        errorKey: 'loadError',
        params: {
          pagination: 1,
          where: this.filtersData()
        },
        successAction: () => {
          this.loading = false;
        }
      });
    },
    reset () {
      this.flights = null;
      this.filters.date_range = {
        startDate: null,
        endDate: null
      };
      this.filters.departure_airport_id = null;
      this.filters.arrival_airport_id = null;
      this.filters.number = '';
      this.filters.trip_reference = '';
    },
    hide () {
      this.reset();
      this.isVisible = false;
      this.loading = false;
      this.importInProgress = false;
    },
    filtersData () {
      let dataToSend = {};

      if (this.filters.date_range.startDate && this.filters.date_range.endDate) {
        dataToSend.date_from = DateUtils.convertDateToString(this.filters.date_range.startDate);
        dataToSend.date_to = DateUtils.convertDateToString(this.filters.date_range.endDate);
      }

      if (this.$refs['flightNumber'] && this.$refs['flightNumber'].item && this.$refs['flightNumber'].item.value) {
        dataToSend.number = this.$refs['flightNumber'].item.value;
      }

      if (this.$refs['tripReference'] && this.$refs['tripReference'].item && this.$refs['tripReference'].item.value) {
        dataToSend.trip_reference = this.$refs['tripReference'].item.value;
      }

      if (this.filters.departure_airport_id) {
        dataToSend.departure_airport_id = this.filters.departure_airport_id;
      }

      if (this.filters.arrival_airport_id) {
        dataToSend.arrival_airport_id = this.filters.arrival_airport_id;
      }

      return dataToSend;
    },
    filterByAirports (option, label, search) {
      return (option.name + ' ' + option.icao).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    importItems (sourceFlightID) {
      this.importInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          import_from_flight_id: sourceFlightID,
          import_to_flight_id: this.flightID
        },
        endpoint: '/trips/' + this.itemType + '/import',
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during import. Please try again',
        successAction: () => {
          this.importInProgress = false;
          this.hide();
          this.$bus.$emit('pax-list-reload-needed');
        },
        failAction: () => {
          this.importInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-import-pax-items-popup', this.show);
  }
}
</script>

<style lang="scss" scoped>
.form-popup /deep/ .reportrange-text {
  padding: 8px 10px;
}
</style>
